import {
    ChangeDetectorRef, DestroyRef, OnDestroy, Pipe, PipeTransform, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    BulkResponse, Category, GetResponse
} from "@shopliftr/common-js/shared";
import {
    Subject, map, takeUntil
} from "rxjs";

import { CategoryApiClientService } from "../../iapi-application/services/api-client/category/category-api-client.service";

/**
 * Transforms an array of category IDs into a comma-separated string of category
 * display names via a single bulk API lookup.
 *
 * **Type:** Impure (`pure: false`) — runs on every change-detection cycle to pick
 * up the cached value once the service responds.
 *
 * Follows the impure-pipe async pattern: `transform()` returns synchronously from
 * a cached value and triggers a fresh change-detection cycle when the service responds.
 * Never inject this pipe — use `CategoryApiClientService.bulkGet$` directly in components.
 *
 * @example
 * ```html
 * {{ categoryIds | mgdCategoryName }}
 * ```
 */
@Pipe({
    name: "mgdCategoryName",
    // Pure must be false in order for the async to work correctly
    // This means that transform will be checked on each change detection cycle
    pure: false
})
export class CategoryNamePipe implements PipeTransform, OnDestroy {

    /**
     * Serialised form of the last IDs array passed into transform, used to
     * detect when the input has changed without triggering needless re-fetches.
     */
    private _lastKey: string;

    /**
     * The last value emitted by the transform function.
     */
    private _lastValue: string;

    /**
     * This subject will fire if we need to cancel an in-flight lookup
     * and start a new one.
     */
    private readonly _stopLookup$ = new Subject<undefined>();

    private readonly _categoryClient = inject(CategoryApiClientService);
    private readonly _cdRef = inject(ChangeDetectorRef);
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Returns a comma-separated string of category names for the given IDs.
     *
     * Issues a single `bulkGet$` request for all IDs. Returns `undefined` on the
     * first tick (before the response arrives) and triggers change detection when
     * the lookup completes. Returns `""` when the input is empty or the service errors.
     *
     * @param categoryIds The category IDs to resolve.
     * @returns Comma-separated category names, or `""` when none are provided.
     */
    transform(categoryIds: Array<string>): string {

        if (!categoryIds?.length) {
            return "";
        }

        const key = categoryIds.join(",");

        if (key !== this._lastKey) {
            // The first call, and any call where the IDs have changed.
            // Cancel any in-flight lookup and start a new subscription.

            this._lastValue = undefined;
            // This will stop any lookup currently running
            this._stopLookup$.next(undefined);
            this._lastKey = key;

            this._categoryClient.bulkGet$(categoryIds).pipe(
                map((response: BulkResponse) => response.results
                    .filter((item) => !item.error)
                    .map((item) => (item.payload as GetResponse<Category>).payload.name)
                    .join(", ")),
                takeUntilDestroyed(this._destroyRef),
                takeUntil(this._stopLookup$)
            )
                .subscribe({
                    next: (names: string) => {
                        this._lastValue = names;
                        // This will cause the component to call the transform function again
                        // The _lastValue will then be returned
                        this._cdRef.markForCheck();
                    },
                    error: () => {
                        this._lastValue = "";
                        // This will cause the component to call the transform function again
                        // The _lastValue will then be returned
                        this._cdRef.markForCheck();
                    }
                });
        }

        return this._lastValue;
    }

    /**
     * Angular onDestroy lifecycle event. Complete the _stopLookup$ subject.
     */
    ngOnDestroy(): void {
        this._stopLookup$.complete();
    }

}
